<script setup lang="ts">
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { addMonths, parse } from 'date-fns'
import { useCurrencyFormat } from '@/utils'
import currency from 'currency.js'
import { MembercardWebapp } from '@/type/MemberCard'
import { formatLocalExpireDate } from '@/global'
import { getMemberCardSpendingSummary } from '@/composables/Patient/usePatientMemberCard'

interface Props {
  showChevron?: boolean
  member: MembercardWebapp
}
const { member, showChevron } = defineProps<Props>()

const router = useRouter()
const route = useRoute()

const spendingSummary = computed(() => {
  return getMemberCardSpendingSummary(member)
})

const handleClickMobile = () => {
  if (!member.next_level_name) return
  router.push({
    name: 'Accum',
    query: {
      ...route.query,
    },
  })
}

const membercardExpireDate = computed(() => {
  if (member && member.expire_date) {
    const membercard = member // for more readable code
    // case is_max level membercard
    if (membercard.is_max && membercard.member_card.min_spending) {
      // หาจำนวนรอบที่ต่อสัญญาในปัจจุบัน โดยเอา total_spending มาหาร ไม่เอาเศษด้วย min_spending
      const renewLap = Math.floor(membercard?.total_spending / membercard.member_card.min_spending)
      if (renewLap > 0) {
        const formatDate = parse(membercard.expire_date, 'dd-MM-yyyy HH:mm', new Date())
        const renewExpireDate = addMonths(formatDate, membercard.member_card.expire_in * renewLap) // expire_in is number of months
        return renewExpireDate
      }
    }
    // case normal expire date
    const formatDate = parse(membercard.expire_date, 'dd-MM-yyyy HH:mm', new Date())
    return formatDate
  }
  // just for prevent bug
  return null
})

// is max expire
const membercardExpireDateAfterRenew = computed(() => {
  if (member?.is_max && membercardExpireDate.value) {
    // calculate only at max level
    return addMonths(membercardExpireDate.value, member.member_card.expire_in)
  }
  return membercardExpireDate.value
})

const processBar = computed(() => {
  // total_spending ไม่มีจ้า
  if (spendingSummary.value.totalSpending && spendingSummary.value.nextLevelMinSpending) {
    return currency(spendingSummary.value.totalSpending)
      .divide(spendingSummary.value.nextLevelMinSpending)
      .multiply(100)
      .toString()
  }
  return 0
})
</script>

<template>
  <div>
    <!-- Lifetime infinite -->
    <div
      v-if="member && member.member_card.name == 'Lifetime Infinite'"
      class="tw-p-3 tw-border tw-border-primary tw-shadow-md tw-bg-[#F0FCFF] tw-rounded-xl tw-mb-1"
    >
      <p class="tw-text-[16px] tw-mb-2">
        {{ $t('membercard.lifetime_infinite') }}
      </p>

      <h1 class="tw-text-[22px] tw-font-extrabold mb-2">
        {{ member.member_card.name.toLocaleUpperCase() }}
      </h1>

      <p class="tw-text-[14px]">{{ $t('membercard.loyalty') }}</p>
    </div>
    <!-- have member card -->
    <div
      v-else-if="member"
      @click="handleClickMobile()"
      class="tw-border tw-border-primary tw-shadow-md tw-grid tw-grid-cols-12 tw-gap-1 tw-bg-[#F0FCFF] tw-rounded-xl tw-mb-1"
    >
      <div :class="[showChevron ? 'tw-col-span-11' : 'tw-col-span-12', 'tw-px-6 tw-p-2.5']">
        <span
          class="bg-primary2 tw-rounded-full tw-p-1 tw-px-2 tw-w-full text-default tw-text-center tw-text-[12px] tw-font-bold"
        >
          {{ $t('membercard.progress.order_amount') }}
        </span>
        <div class="tw-my-1 tw-mt-2 md:tw-my-2 justify-items-end">
          <v-avatar class="tw-me-1.5" color="#919295" size="18">
            <span class="tw-text-[12px]">฿</span>
          </v-avatar>
          <span v-if="member.is_max" class="tw-me-1.5 tw-font-semibold tw-text-[#919295]">
            {{ useCurrencyFormat(spendingSummary.totalSpending) }}/
            {{ useCurrencyFormat(spendingSummary.nextLevelMinSpending) }}
          </span>
          <span v-else class="tw-me-1.5 tw-font-semibold tw-text-[#919295]">
            {{ useCurrencyFormat(spendingSummary.totalSpending) }}/
            {{ useCurrencyFormat(spendingSummary.nextLevelMinSpending) }}
          </span>
        </div>

        <div class="tw-text-[#76777B] tw-text-[12px]">
          <v-progress-linear
            class="tw-mb-2"
            :model-value="processBar"
            bg-color="blue-grey"
            color="primary2"
            height="3"
            rounded
          />
          <p v-if="member.is_max">
            {{
              $t('membercard.progress.buy_add_to_renew', {
                num: useCurrencyFormat(spendingSummary.totalSpendingLeft),
                date: formatLocalExpireDate(membercardExpireDateAfterRenew),
              })
            }}
          </p>
          <p v-else>
            {{
              $t('membercard.progress.buy_add_to_unlock', {
                num: useCurrencyFormat(spendingSummary.totalSpendingLeft),
                next_level: member.next_level_name,
              })
            }}
          </p>
        </div>
      </div>

      <div
        v-if="showChevron"
        class="tw-col-span-1 tw-flex tw-items-center tw-justify-center bg-primary tw-rounded-tr-xl tw-rounded-br-xl tw-border-l tw-border-primary"
      >
        <v-icon color="#ffffff">mdi-chevron-right</v-icon>
      </div>
    </div>
  </div>
</template>
